'use client'

import { useEffect, useMemo, useState } from 'react'
import translateText from 'app/lib/translatedText'
import Image from 'next/image'
import PolygonButtonWithBorder from 'app/[lang]/pfl-landing-test/v2/PolygonButtonWithBorder'
import { RiArrowRightUpFill } from 'react-icons/ri'
import { useRouter } from 'next/navigation'
import { Button } from 'components/ui/button'

import {
    getOrganisationCompetitions,
    getOrganisationFederations,
    getCompetitionById,
    getFederationById
} from './actions'
import PolygonCard from './components/PolygonCard'
import StandardCard from './components/StandardCard'
import { PolygonWidgetWrapper } from 'components/WidgetWrapper'

const FALLBACK_IMAGE = '/images/pfl-testing/comp_map.png'
const FALLBACK_TITLE = '200+ active competitions, $100,000+ in prizes to be won.'
const FALLBACK_DESCRIPTION =
    'Compete against local and global teams in diverse tournament formats, from quick matches to full seasons. Find your perfect fit.'

const DEFAULT_BUTTON_LINKS: Record<'competition' | 'federation', string> = {
    competition: 'explore/competitions',
    federation: 'explore/federations'
}

const DEFAULT_LIMIT = 4

type ContentItem = IFederation | ICompetition
type ContentType = 'competition' | 'federation'

interface FullContentWidgetProps {
    lang: string
    data?: IFullContentWidget
    config?: IDomainConfig
    styleType?: WidgetStyleType
    /**
     * Pre-fetched widget data from server component.
     * If provided, this widget will use it instead of making client-side API calls.
     */
    widgetData?: import('app/types/widget-data').FullContentWidgetData | null
}

const normalizeLimit = (limit?: number) => {
    if (!limit || Number.isNaN(limit) || limit <= 0) return DEFAULT_LIMIT
    return Math.min(limit, DEFAULT_LIMIT)
}

const sanitizeLink = (link?: string) => {
    if (!link) return ''
    const trimmed = link.trim()
    if (/^https?:\/\//.test(trimmed) || trimmed.startsWith('//')) return trimmed
    return trimmed.replace(/^\//, '')
}

const FullContentWidget = ({ lang, data, config, styleType: styleTypeProp, widgetData }: FullContentWidgetProps) => {
    const [items, setItems] = useState<ContentItem[]>([])
    const [imageError, setImageError] = useState(false)
    const organisationId = config?.organisation_id ?? ''
    const dataType: ContentType = data?.dataType === 'competition' ? 'competition' : 'federation'
    const limit = normalizeLimit(data?.limit)
    // Only use fallback image if there's no saved data at all
    // If data exists but image is empty, show empty (no fallback)
    const imageSrc = data ? data.image || '' : FALLBACK_IMAGE
    // Use fallback if image failed to load
    const displayImageSrc = imageError && imageSrc !== FALLBACK_IMAGE ? FALLBACK_IMAGE : imageSrc
    const widgetStyleType: WidgetStyleType = styleTypeProp || (data?.styleType as WidgetStyleType) || 'default'
    const router = useRouter()

    // Reset image error when image source changes
    useEffect(() => {
        setImageError(false)
    }, [imageSrc])

    useEffect(() => {
        // If pre-fetched widget data is available, use it instead of fetching
        if (widgetData) {
            if (dataType === 'competition' && widgetData.competitions) {
                setItems(widgetData.competitions)
                return
            } else if (dataType === 'federation' && widgetData.federations) {
                setItems(widgetData.federations)
                return
            }
        }

        if (!organisationId) {
            setItems([])
            return
        }

        let isMounted = true

        const fetchContent = async () => {
            // If selectedIds are provided, fetch by IDs using Promise.all
            if (data?.selectedIds && Array.isArray(data.selectedIds) && data.selectedIds.length > 0) {
                const fetchPromises = data.selectedIds.map(id => {
                    if (dataType === 'competition') {
                        return getCompetitionById(id)
                    } else {
                        return getFederationById(id)
                    }
                })

                try {
                    const responses = await Promise.all(fetchPromises)
                    if (!isMounted) return

                    const fetchedItems: ContentItem[] = []
                    responses.forEach(response => {
                        if (response?.success && response?.data) {
                            fetchedItems.push(response.data)
                        }
                    })

                    setItems(fetchedItems)
                } catch (error) {
                    console.error('Error fetching items by IDs:', error)
                    if (!isMounted) return
                    setItems([])
                }
                return
            }

            // Fallback to old behavior if no selectedIds (for backward compatibility)
            if (dataType === 'competition') {
                const response = await getOrganisationCompetitions(organisationId, { limit })
                if (!isMounted) return
                if (response?.success && Array.isArray(response?.data)) setItems(response.data)
                else setItems([])
                return
            }

            const response = await getOrganisationFederations(organisationId, { limit })
            if (!isMounted) return
            if (response?.success && Array.isArray(response?.data)) setItems(response.data)
            else setItems([])
        }

        fetchContent()

        return () => {
            isMounted = false
        }
    }, [organisationId, dataType, limit, data?.selectedIds])

    // Only use fallbacks when there's NO saved data at all (new widget scenario)
    // Always prefer saved data over fallbacks
    const hasSavedData = !!data

    const translatedTitle = data?.title ? translateText(lang, data.title) : ''
    const translatedDescription = data?.description ? translateText(lang, data.description) : ''
    const translatedButtonText = data?.button?.text ? translateText(lang, data.button.text) : ''

    // Only use fallback if there's no saved data AND no translation
    const displayTitle = hasSavedData
        ? translatedTitle && translatedTitle !== '<ERROR>'
            ? translatedTitle
            : ''
        : translatedTitle && translatedTitle !== '<ERROR>'
          ? translatedTitle
          : FALLBACK_TITLE

    const displayDescription = hasSavedData
        ? translatedDescription && translatedDescription !== '<ERROR>'
            ? translatedDescription
            : ''
        : translatedDescription && translatedDescription !== '<ERROR>'
          ? translatedDescription
          : FALLBACK_DESCRIPTION

    const defaultButtonText = `View all ${dataType === 'competition' ? 'competitions' : 'federations'}`
    // Button text always falls back to default if empty, since a button needs visible text for usability
    const buttonTitle =
        translatedButtonText && translatedButtonText !== '<ERROR>' ? translatedButtonText : defaultButtonText

    // Use saved button link if available, otherwise fall back to default
    // This ensures the button always has a link if it's displayed
    const savedButtonLink = data?.button?.link
    const sanitizedSavedLink = sanitizeLink(savedButtonLink)
    const buttonLink = sanitizedSavedLink || sanitizeLink(DEFAULT_BUTTON_LINKS[dataType]) || DEFAULT_BUTTON_LINKS[dataType]

    const handleButtonClick = () => {
        if (!buttonLink) return
        if (/^https?:\/\//.test(buttonLink) || buttonLink.startsWith('//')) {
            window.open(buttonLink, '_blank', 'noopener,noreferrer')
            return
        }

        const href = buttonLink.startsWith('/') ? buttonLink : `/${buttonLink}`
        router.push(`/${lang}${href}`)
    }

    // If selectedIds are provided, respect the order of selectedIds, otherwise use limit
    const itemsToRender = useMemo(() => {
        if (data?.selectedIds && Array.isArray(data.selectedIds) && data.selectedIds.length > 0) {
            // Maintain the order of selectedIds
            const orderedItems: ContentItem[] = []
            data.selectedIds.forEach(id => {
                const item = items.find(i => i.id === id)
                if (item) {
                    orderedItems.push(item)
                }
            })
            return orderedItems
        }
        return items.slice(0, limit)
    }, [items, limit, data?.selectedIds])

    // Polygon style rendering
    if (widgetStyleType === 'polygon_style') {
        return (
            <PolygonWidgetWrapper>
                <div className='grid md:grid-cols-2 gap-4 md:gap-x-6 items-center mb-12 w-full mx-auto px-4 md:px-0'>
                    <div className='flex flex-col gap-y-2 md:gap-y-4 extra_large:gap-y-6 w-full '>
                        {displayImageSrc ? (
                            <div className='relative md:h-[400px] w-full md:max-w-[680px] extra_large:max-w-none extra_large:w-full extra_large:h-[800px] '>
                                <Image
                                    src={displayImageSrc}
                                    alt='competition map'
                                    fill
                                    className='object-contain extra_large:object-cover'
                                    sizes="(max-width: 768px) 100vw, 680px"
                                    onError={() => {
                                        if (!imageError && displayImageSrc !== FALLBACK_IMAGE) {
                                            setImageError(true)
                                        }
                                    }}
                                />
                            </div>
                        ) : (
                            <div className='relative md:h-[400px] w-full md:max-w-[680px] extra_large:max-w-none extra_large:w-full extra_large:h-[800px] flex items-center justify-center bg-muted rounded-lg border border-dashed border-muted-foreground/30'>
                                <div className='text-center p-4'>
                                    <p className='text-sm text-muted-foreground'>Theme image not available</p>
                                </div>
                            </div>
                        )}
                        <div className='max-w-[95%] gap-y-2 md:gap-y-4 flex flex-col extra_large:gap-y-6'>
                            {displayTitle && (
                                <p className='md:text-4xl text-3xl extra_large:text-5xl font-extrabold text-foreground leading-normal '>
                                    {displayTitle}
                                </p>
                            )}
                            {displayDescription && (
                                <p className='text-sm md:text-base extra_large:text-xl'>{displayDescription}</p>
                            )}
                        </div>
                        <div className='flex gap-x-3 items-center'>
                            <PolygonButtonWithBorder
                                className='h-12'
                                link={buttonLink}
                                title={buttonTitle}
                                Icon={
                                    <div className='grid w-fit'>
                                        <RiArrowRightUpFill className='size-5 text-foreground' />
                                        <div className='!border-accent-foreground !border-b w-4 flex' />
                                    </div>
                                }
                            />
                        </div>
                    </div>
                    <div className='grid grid-cols-2 w-full gap-2 md:gap-4'>
                        {itemsToRender.map(item => (
                            <PolygonCard key={item.id} data={item} dataType={dataType} />
                        ))}
                    </div>
                </div>
            </PolygonWidgetWrapper>
        )
    } else if (widgetStyleType === 'default' || !widgetStyleType) {
        // Standard style rendering
        return (
            <div className='px-4 md:p-8'>
                <div className='grid grid-cols-1 lg:grid-cols-2 items-center mb-12 w-full mx-auto gap-4 md:gap-x-6 px-4 md:px-0'>
                    <div className='grid w-full gap-y-2 md:gap-y-3'>
                        {displayImageSrc ? (
                            <div className='relative w-full md:h-[300px] md:max-w-[500px]'>
                                <Image
                                    src={displayImageSrc}
                                    alt='competition map'
                                    fill
                                    className='object-contain'
                                    sizes="(max-width: 768px) 100vw, 500px"
                                    onError={() => {
                                        if (!imageError && displayImageSrc !== FALLBACK_IMAGE) {
                                            setImageError(true)
                                        }
                                    }}
                                />
                            </div>
                        ) : (
                            <div className='relative w-full md:h-[300px] md:max-w-[500px] flex items-center justify-center bg-muted rounded-lg border border-dashed border-muted-foreground/30'>
                                <div className='text-center p-4'>
                                    <p className='text-sm text-muted-foreground'>Theme image not available</p>
                                </div>
                            </div>
                        )}
                        <div className='max-w-[95%] flex flex-col gap-y-2 md:gap-y-3'>
                            {displayTitle && (
                                <p className='font-extrabold text-foreground leading-normal text-2xl md:text-3xl lg:text-4xl'>
                                    {displayTitle}
                                </p>
                            )}
                            {displayDescription && <p className='text-sm md:text-base'>{displayDescription}</p>}
                        </div>
                        <div className='flex gap-x-3 items-center'>
                            <Button
                                type='button'
                                className='h-12 px-6 text-base font-semibold bg-foreground text-zinc-900 hover:bg-foreground/90'
                                onClick={handleButtonClick}
                            >
                                <span className='flex items-center gap-2'>
                                    {buttonTitle}
                                    <RiArrowRightUpFill className='size-5' />
                                </span>
                            </Button>
                        </div>
                    </div>
                    <div className='grid grid-cols-2 w-full gap-2 md:gap-4 self-start md:self-stretch'>
                        {itemsToRender.map(item => (
                            <StandardCard key={item.id} data={item} dataType={dataType} />
                        ))}
                    </div>
                </div>
            </div>
        )
    } else {
        // Widget type not available
        return (
            <div className='px-4 md:p-8'>
                <div className='relative w-full min-h-[400px] flex items-center justify-center'>
                    <div className='text-center space-y-4'>
                        <h2 className='text-2xl font-bold text-foreground'>Widget Type Not Available</h2>
                        <p className='text-muted-foreground'>
                            The requested full content widget style type is not supported.
                        </p>
                    </div>
                </div>
            </div>
        )
    }
}

export default FullContentWidget
